import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormControlLabel,
  Switch,
  Checkbox,
  Tooltip,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  CardHeader,
  Avatar,
  Stack,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  IconButton,
  Link,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  AutoFixHigh as AutoFixHighIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  Upgrade as UpgradeIcon,
  Add as AddIcon,
  Screenshot as ScreenshotIcon,
} from '@mui/icons-material';
import {
  evaluateInjectGate,
  injectGateErrorMessage,
  isInjectGateAbortError,
} from '../services/obpAiService';
import {
  getListingGroupKey,
  groupReviewItems,
  type GroupedProductEntry,
} from '../utils/reviewListingGroups';
import {
  decisionChipColor,
  gateChipColor,
  isBrandIpMerchandiseInject,
  resolveCatalogProductName,
  type InjectGateResult,
} from '../utils/injectGate';
import { injectReviewListing } from '../utils/injectReviewListing';
import {
  AUTO_PROCESS_CONCURRENCY,
  AUTO_PROCESS_GATE_TIMEOUT_MS,
  CATALOG_FETCH_SIZE,
  REVIEWS_PAGE_SIZE,
  catalogNamesFrom,
  createBrandCatalogCache,
  createTaskKeywordCache,
  listingBrandFromGroup,
  mapPool,
  heldGroupsToReject,
  missingAutoInjectFields,
  mostCommonHoldReason,
  nextReviewsFetchOffset,
  type AutoProcessResult,
} from '../utils/reviewAutoProcess';
import {
  postMarkGroupReviewed,
  postMarkGroupsReviewed,
  stripGroupFromReviewItems,
} from '../utils/reviewMarkReviewed';
import { canReuseInjectGate, parseStoredInjectGate } from '../utils/reviewStoredGate';
import {
  AUTO_PROCESS_LOCK_HEARTBEAT_MS,
  acquireAutoProcessLock,
  getAutoProcessLockStatus,
  heartbeatAutoProcessLock,
  newAutoProcessLockOwner,
  releaseAutoProcessLock,
  releaseAutoProcessLockBeacon,
} from '../utils/reviewAutoProcessLock';
import { useScrapingTasks } from '../hooks/useScrapingTasks';
import {
  useMarketplaceDefinitions,
  type MarketplaceDefinition,
} from '../hooks/useMarketplaceDefinitions';
import { useSearchParams } from 'react-router-dom';
import {
  storeClient,
  endpoints,
  apiClient,
  importerClient,
  isGodUser,
  canUseReviewAi,
  storeAuth,
} from '../api/client';
import { parsePrice, extractItemNumberFromUrl } from '../utils/listingTransformer';
import { matchListingToCatalogProduct } from '../utils/catalogProductMatch';
import { ListingState } from '../types/brandtraxListing';
import {
  REVIEW_CLASSIFICATION_OPTIONS,
  validateClassificationBeforeSubmit,
  infringementOptionsToSelectOptions,
  distributionOptionsToSelectOptions,
  allowedReasonValuesFromOptions,
  allowedDistributionValuesFromOptions,
  canApplyAiInfringementForState,
  loadApplyAiInfringementPreference,
  saveApplyAiInfringementPreference,
  type BrandInfringementOptionsResponse,
} from '../utils/infringementReasons';
import { loadStoreProductsForBrand, type StoreCatalogProduct } from '../utils/storeCatalog';
import { assembleMarketplaceOptionLabel } from '../utils/taskGroupAssemble';
import { reviewPlatformOptions } from '../utils/reviewPlatformFilter';
import { getMarketplaceLogo, matchMarketplaceDefinitionForUrl } from '../utils/marketplaceMatcher';
import {
  collectListingSellers,
  sellerDisplayName,
  sellerIdentifier,
} from '../utils/listingSellers';

function normalizeListingUrl(input?: string): string {
  if (!input || typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (!trimmed) return '';
  try {
    const url = new URL(trimmed);
    const host = url.hostname.toLowerCase();
    const path = (url.pathname || '/').replace(/\/+$/, '') || '/';
    return `${host}${path}`;
  } catch {
    return trimmed.toLowerCase();
  }
}

interface ReviewListItem {
  id: string;
  task_id: string;
  url: string;
  crawled_at: string;
  // Include task brand/product from API response for store lookup
  brand_name?: string;
  product_name?: string;
  marketplace_definition_id?: string | null;
  products: Array<{
    title?: string;
    price?: string;
    url?: string;
    image?: string;
    marketplace?: string;
    brand?: string;
    product?: string;
    screenshot_url?: string;
    itemNumber?: string;
    item_number?: string;
    relatedItems?: string[];
    related_items?: string[];
    // Seller-related fields
    seller?: string;
    seller_name?: string;
    seller_rating?: string;
    location?: string;
    seller_store_url?: string;
    sellers?: Array<{
      name?: string;
      url?: string;
      rating?: string;
      location?: string;
      store_url?: string;
      screenshot_url?: string;
    }>;
  }>;
}

interface ListingSummary {
  state?: string;
  status?: string;
  url?: string;
  archived?: boolean;
}

const Reviews: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { getReviews, getTaskResults, getTask } = useScrapingTasks();
  const { data: marketplaceDefinitions = [] } = useMarketplaceDefinitions();
  const [brand, setBrand] = useState<string>(searchParams.get('brand') || '');
  const [product, setProduct] = useState('');
  const [catalogProducts, setCatalogProducts] = useState<StoreCatalogProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    const fromQuery = searchParams.get('brand');
    if (fromQuery) setBrand(fromQuery);
  }, [searchParams]);
  const [stage, setStage] = useState<string>('stage2_product_page');
  const [keyword, setKeyword] = useState<string>('');
  const [keywordDebounced, setKeywordDebounced] = useState<string>('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<MarketplaceDefinition[]>([]);
  const [brands, setBrands] = useState<Array<{ name: string }>>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ReviewListItem[]>([]);
  // Selected group key (one card = one listing with possibly multiple sellers)
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  // Legacy shape kept for code that still expects parentId/index; derived from selected group
  const [, setSelectedListingId] = useState<string | null>(null);
  const generateListingId = (parentId: string, index: number) => {
    return `${parentId}-${index}`;
  };
  const [injectGate, setInjectGate] = useState<InjectGateResult | null>(null);
  const injectGateRef = useRef<InjectGateResult | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [autoAi, setAutoAi] = useState<boolean>(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchStats, setBatchStats] = useState({
    injected: 0,
    rejected: 0,
    held: 0,
    working: 0,
    holdReason: '' as string,
  });
  const [heldGroups, setHeldGroups] = useState<GroupedProductEntry[]>([]);
  const [rejectingHeld, setRejectingHeld] = useState(false);
  const [rejectHeldOpen, setRejectHeldOpen] = useState(false);
  const rejectingHeldRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [storeProduct, setStoreProduct] = useState<any>(null);
  const [storeProducts, setStoreProducts] = useState<any[]>([]);
  const [storeProductsLoading, setStoreProductsLoading] = useState(false);
  const [overrideBrand, setOverrideBrand] = useState<string | null>(null);
  const [overrideProduct, setOverrideProduct] = useState<string | null>(null);
  const [listingClassification, setListingClassification] = useState<ListingState>(
    ListingState.NEW
  );
  const [infringementReason, setInfringementReason] = useState<string>('');
  const [applyAiInfringement, setApplyAiInfringement] = useState(false);
  const [infringementSelectOptions, setInfringementSelectOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [distributionSelectOptions, setDistributionSelectOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [infringementOptionsLoading, setInfringementOptionsLoading] = useState(false);
  const [brandInfringementOptions, setBrandInfringementOptions] =
    useState<BrandInfringementOptionsResponse | null>(null);
  const [storeLoading, setStoreLoading] = useState(false);
  const [marketplaceLogos, setMarketplaceLogos] = useState<Map<string, string>>(new Map());
  const [authorizedListingKeys, setAuthorizedListingKeys] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const canUseAi = canUseReviewAi();
  const batchStopRef = useRef(false);
  const batchAbortRef = useRef<AbortController | null>(null);
  const batchRunningRef = useRef(false);
  const lockOwnerRef = useRef<string | null>(null);
  const lockBrandRef = useRef<string | null>(null);
  const [foreignLockMessage, setForeignLockMessage] = useState('');
  const catalogSettledRef = useRef(true);
  const selectedGroupKeyRef = useRef<string | null>(null);
  const selectedProductRef = useRef<any>(null);
  const allProductsRef = useRef<GroupedProductEntry[]>([]);
  const itemsRef = useRef<ReviewListItem[]>([]);
  const listingProductRef = useRef('');
  const reviewBrandNameRef = useRef('');
  const listingBrandRef = useRef('');
  const storeProductsRef = useRef<any[]>([]);
  const storeProductRef = useRef<any>(null);
  const overrideProductRef = useRef<string | null>(null);
  const overrideBrandRef = useRef<string | null>(null);
  const handleAnalyzeRef = useRef<(() => Promise<InjectGateResult | null>) | null>(null);
  const keywordsForTask = useMemo(() => createTaskKeywordCache(getTask), [getTask]);
  const getReviewsRef = useRef(getReviews);
  const brandRef = useRef(brand);
  const productRef = useRef(product);
  const stageRef = useRef(stage);
  const keywordDebouncedRef = useRef(keywordDebounced);
  const selectedPlatformsRef = useRef<MarketplaceDefinition[]>([]);

  const findMarketplaceDefinitionForUrl = useCallback(
    (url?: string) => matchMarketplaceDefinitionForUrl(url, marketplaceDefinitions),
    [marketplaceDefinitions]
  );

  const platformOptions = useMemo(
    () => reviewPlatformOptions(marketplaceDefinitions),
    [marketplaceDefinitions]
  );

  const marketplaceIdsFrom = useCallback((platforms: MarketplaceDefinition[]) => {
    const ids = platforms.map(p => p.id).filter(Boolean);
    return ids.length ? ids : undefined;
  }, []);

  useEffect(() => {
    const onPageHide = () => {
      const owner = lockOwnerRef.current;
      const lockedBrand = lockBrandRef.current;
      if (owner && lockedBrand) {
        releaseAutoProcessLockBeacon(lockedBrand, owner);
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  useEffect(() => {
    if (!brand || batchRunning) {
      if (batchRunning) setForeignLockMessage('');
      return;
    }
    let cancelled = false;
    const check = async () => {
      const status = await getAutoProcessLockStatus(brand);
      if (cancelled) return;
      setForeignLockMessage(
        status.held ? status.message || `Auto-process is already running for ${brand}.` : ''
      );
    };
    check();
    const timer = window.setInterval(check, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [brand, batchRunning]);

  const reviewFiltersKey = `${brand}|${product}|${stage}|${keywordDebounced}|${selectedPlatforms
    .map(platform => platform.id)
    .join(',')}`;
  const reviewFiltersKeyRef = useRef(reviewFiltersKey);
  useEffect(() => {
    if (reviewFiltersKeyRef.current === reviewFiltersKey) return;
    reviewFiltersKeyRef.current = reviewFiltersKey;
    if (batchRunningRef.current || rejectingHeldRef.current) return;
    setHeldGroups([]);
    setRejectHeldOpen(false);
    setBatchStats({ injected: 0, rejected: 0, held: 0, working: 0, holdReason: '' });
  }, [reviewFiltersKey]);

  // Load brands for filter — GOD users see all brands, others see assigned brands
  useEffect(() => {
    const load = async () => {
      setLoadingBrands(true);
      try {
        let brandsList: Array<{ name: string }> = [];
        if (isGodUser()) {
          const res = await storeClient.get(endpoints.storeAllBrandNames());
          const names = res?.data || [];
          brandsList = names.map((name: string) => ({ name }));
        } else {
          const res = await storeClient.get(endpoints.storeBrands());
          const items = res?.data?.items || res?.data?.content || res?.data || [];
          brandsList = items.map((b: any) => ({ name: b.name }));
        }
        brandsList.sort((a, b) => a.name.localeCompare(b.name));
        setBrands(brandsList);
      } catch (_) {
        setBrands([]);
      } finally {
        setLoadingBrands(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!brand) {
      setCatalogProducts([]);
      setProduct('');
      setLoadingProducts(false);
      return;
    }
    let cancelled = false;
    setCatalogProducts([]);
    setProduct('');
    setLoadingProducts(true);
    loadStoreProductsForBrand(brand)
      .then(list => {
        if (cancelled) return;
        const sorted = list.slice().sort((a, b) => a.name.localeCompare(b.name));
        setCatalogProducts(sorted);
        setProduct(prev => (prev && sorted.some(item => item.name === prev) ? prev : ''));
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogProducts([]);
          setProduct('');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingProducts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brand]);

  // Debounce keyword for search
  useEffect(() => {
    const t = setTimeout(() => setKeywordDebounced(keyword), 300);
    return () => clearTimeout(t);
  }, [keyword]);

  // Load reviews
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setHasMore(true);
      const q = keywordDebounced.trim() || undefined;
      const res = await getReviews(
        brand || undefined,
        stage,
        50,
        0,
        q,
        marketplaceIdsFrom(selectedPlatforms),
        product || undefined
      );
      setItems(res.items || []);
      setSelectedGroupKey(null);
      setInjectGate(null);
      setLoading(false);
    };
    load();
  }, [brand, product, stage, keywordDebounced, selectedPlatforms, getReviews, marketplaceIdsFrom]);

  // Load marketplace logos for all items
  useEffect(() => {
    const loadMarketplaceLogos = async () => {
      if (items.length === 0) return;

      const logoMap = new Map<string, string>();
      const uniqueUrls = [...new Set(items.map(item => item.url))];

      for (const url of uniqueUrls) {
        try {
          const logoUrl = await getMarketplaceLogo(url);
          if (logoUrl) {
            logoMap.set(url, logoUrl);
          }
        } catch (error) {
          console.error('Failed to load marketplace logo for:', url, error);
        }
      }

      setMarketplaceLogos(logoMap);
    };

    loadMarketplaceLogos();
  }, [items]);

  // Load more reviews for infinite scroll
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const q = keywordDebounced.trim() || undefined;
      const res = await getReviews(
        brand || undefined,
        stage,
        50,
        items.length,
        q,
        marketplaceIdsFrom(selectedPlatforms),
        product || undefined
      );
      if (res.items && res.items.length > 0) {
        setItems(prev => [...prev, ...res.items]);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more reviews:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [
    brand,
    product,
    stage,
    keywordDebounced,
    selectedPlatforms,
    getReviews,
    marketplaceIdsFrom,
    items.length,
    loadingMore,
    hasMore,
  ]);

  // Infinite scroll handler
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const isNearEnd = scrollLeft + clientWidth >= scrollWidth - 100; // 100px threshold

      if (isNearEnd && hasMore && !loadingMore) {
        loadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loadMore, hasMore, loadingMore]);

  const allProducts = useMemo(() => groupReviewItems(items), [items]);
  const rejectableHeld = useMemo(
    () =>
      heldGroupsToReject({
        held: heldGroups,
        loadedGroupKeys: allProducts.map(group => group.groupKey),
        feedFullyLoaded: !hasMore,
      }),
    [heldGroups, allProducts, hasMore]
  );

  useEffect(() => {
    let cancelled = false;

    const loadAuthorizedSellerListings = async () => {
      if (allProducts.length === 0) {
        setAuthorizedListingKeys(new Set());
        return;
      }

      const uniqueUrls = Array.from(
        new Set(
          allProducts
            .map(p => String(p.product?.url || p.product?.current_url || '').trim())
            .filter(Boolean)
        )
      );

      if (uniqueUrls.length === 0) {
        setAuthorizedListingKeys(new Set());
        return;
      }

      try {
        const response = await importerClient.post(
          endpoints.importRaceListingSummaries,
          uniqueUrls
        );
        if (cancelled) return;

        const summaries: ListingSummary[] = Array.isArray(response?.data) ? response.data : [];
        const normalizedAuthorizedUrls = new Set(
          summaries
            .filter(s => s?.state === 'AUTHORIZED_SELLER')
            .map(s => normalizeListingUrl(s.url))
            .filter(Boolean)
        );

        const groupKeys = new Set(
          allProducts
            .filter(entry => {
              const url = String(entry.product?.url || entry.product?.current_url || '');
              return normalizedAuthorizedUrls.has(normalizeListingUrl(url));
            })
            .map(entry => entry.groupKey)
        );

        setAuthorizedListingKeys(groupKeys);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading authorized seller listing summaries:', error);
          setAuthorizedListingKeys(new Set());
        }
      }
    };

    loadAuthorizedSellerListings();

    return () => {
      cancelled = true;
    };
  }, [allProducts]);

  const selectedGroup = useMemo(
    () =>
      selectedGroupKey ? (allProducts.find(g => g.groupKey === selectedGroupKey) ?? null) : null,
    [allProducts, selectedGroupKey]
  );
  const selectedProduct = useMemo(() => selectedGroup?.product ?? null, [selectedGroup]);
  const selectedParent = useMemo(() => {
    if (!selectedGroup) return null;
    return items.find(i => i.id === selectedGroup.parentId) ?? null;
  }, [items, selectedGroup]);
  const selectedIsAuthorizedSeller = useMemo(() => {
    if (!selectedGroup) return false;
    return authorizedListingKeys.has(selectedGroup.groupKey);
  }, [authorizedListingKeys, selectedGroup]);

  // Brand/product for the listing under review (not the page-level brand filter)
  const listingBrand = useMemo(() => {
    return (
      ((selectedProduct as any)?.brand as string) ||
      (selectedGroup?.taskBrand as string) ||
      (selectedParent?.brand_name as string) ||
      ''
    ).trim();
  }, [selectedProduct, selectedGroup, selectedParent]);

  const listingProductFromListing = useMemo(() => {
    return (((selectedProduct as any)?.product as string) || '').trim();
  }, [selectedProduct]);

  const catalogMatchedProduct = useMemo(() => {
    if (!selectedProduct || !storeProducts.length) return '';
    return matchListingToCatalogProduct(selectedProduct, storeProducts, listingBrand)?.name || '';
  }, [selectedProduct, storeProducts, listingBrand]);

  const listingProduct = useMemo(() => {
    return (
      listingProductFromListing ||
      catalogMatchedProduct ||
      (selectedGroup?.taskProduct as string) ||
      (selectedParent?.product_name as string) ||
      ''
    ).trim();
  }, [listingProductFromListing, catalogMatchedProduct, selectedGroup, selectedParent]);

  const reviewBrandName = useMemo(() => {
    return (
      overrideBrand ??
      (listingBrand || String((selectedProduct as any)?.brand || ''))
    ).trim();
  }, [overrideBrand, listingBrand, selectedProduct]);

  injectGateRef.current = injectGate;
  selectedProductRef.current = selectedProduct;
  listingProductRef.current = listingProduct;
  reviewBrandNameRef.current = reviewBrandName;
  listingBrandRef.current = listingBrand;
  storeProductsRef.current = storeProducts;
  storeProductRef.current = storeProduct;
  overrideProductRef.current = overrideProduct;
  overrideBrandRef.current = overrideBrand;

  useEffect(() => {
    if (!reviewBrandName) {
      setBrandInfringementOptions(null);
      setInfringementSelectOptions([]);
      return;
    }
    let cancelled = false;
    setInfringementOptionsLoading(true);
    (async () => {
      try {
        const bearer = await storeAuth.getStoreBearerForRequest();
        if (cancelled) return;
        if (!bearer) {
          setBrandInfringementOptions(null);
          setInfringementSelectOptions([]);
          return;
        }
        const res = await storeClient.get(endpoints.storeBrandInfringementOptions(reviewBrandName));
        if (cancelled) return;
        const data = res.data as BrandInfringementOptionsResponse;
        setBrandInfringementOptions(data);
        setInfringementSelectOptions(infringementOptionsToSelectOptions(data));
        setDistributionSelectOptions(distributionOptionsToSelectOptions(data));
        setInfringementReason(prev => {
          if (prev && data.reasons?.some(r => r.value === prev)) {
            return prev;
          }
          if (prev && data.distributionReasons?.some(r => r.value === prev)) {
            return prev;
          }
          return '';
        });
      } catch {
        if (!cancelled) {
          setBrandInfringementOptions(null);
          setInfringementSelectOptions([]);
          setDistributionSelectOptions([]);
        }
      } finally {
        if (!cancelled) {
          setInfringementOptionsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewBrandName]);

  // Validation for injecting listing into BrandTrax (all required fields must be present)
  const injectValidation = useMemo(() => {
    if (!selectedProduct) {
      return { canInject: false, missingFields: [] as string[], reason: 'No listing selected' };
    }
    if ((selectedProduct as any)?.stage === 'search') {
      return {
        canInject: false,
        missingFields: [] as string[],
        reason: 'Upgrade to full product first (↑)',
      };
    }

    const effectiveBrand = (
      overrideBrand ??
      (listingBrand || String((selectedProduct as any).brand || ''))
    ).trim();
    const analystProduct = (overrideProduct ?? '').trim();
    const brandIpMerchandise = isBrandIpMerchandiseInject(injectGate) && !analystProduct;
    const effectiveProduct = brandIpMerchandise
      ? ''
      : (
          overrideProduct ??
          (listingProduct || String((selectedProduct as any).product || ''))
        ).trim();

    const missingFields: string[] = [];
    if (!String(selectedProduct.url || '').trim()) missingFields.push('URL');
    if (!String(selectedProduct.title || '').trim()) missingFields.push('Title');
    const seller = (selectedProduct as any).seller || (selectedProduct as any).seller_name;
    if (!seller || !String(seller).trim()) missingFields.push('Seller');
    if (!effectiveBrand) missingFields.push('Brand');
    if (!effectiveProduct && !brandIpMerchandise) missingFields.push('Store product');

    const classificationError = validateClassificationBeforeSubmit(
      listingClassification,
      infringementReason,
      allowedReasonValuesFromOptions(brandInfringementOptions),
      allowedDistributionValuesFromOptions(brandInfringementOptions)
    );
    if (classificationError) {
      missingFields.push(
        listingClassification === ListingState.UNAUTHORIZED_DISTRIBUTION
          ? 'Distribution reason'
          : 'Infringement reason'
      );
    }

    const canInject = missingFields.length === 0;
    return {
      canInject,
      missingFields,
      reason: canInject
        ? 'Send to BrandTrax'
        : `Missing required fields: ${missingFields.join(', ')}`,
    };
  }, [
    selectedProduct,
    listingBrand,
    listingProduct,
    overrideBrand,
    overrideProduct,
    listingClassification,
    infringementReason,
    brandInfringementOptions,
    injectGate,
  ]);
  const injectValidationRef = useRef(injectValidation);
  injectValidationRef.current = injectValidation;

  selectedGroupKeyRef.current = selectedGroupKey;
  allProductsRef.current = allProducts;
  itemsRef.current = items;
  getReviewsRef.current = getReviews;
  brandRef.current = brand;
  productRef.current = product;
  stageRef.current = stage;
  keywordDebouncedRef.current = keywordDebounced;
  selectedPlatformsRef.current = selectedPlatforms;

  // Reset manual overrides when the reviewer selects a different listing
  useEffect(() => {
    catalogSettledRef.current = false;
    setOverrideBrand(null);
    setOverrideProduct(null);
    setListingClassification(ListingState.NEW);
    setInfringementReason('');
    setApplyAiInfringement(loadApplyAiInfringementPreference());
  }, [selectedGroupKey]);

  useEffect(() => {
    if (!canApplyAiInfringementForState(listingClassification)) {
      setApplyAiInfringement(false);
    }
  }, [listingClassification]);

  // Fetch store products for the listing's brand; load matched product when name is known
  useEffect(() => {
    const fetchStoreProduct = async () => {
      if (!selectedProduct || !listingBrand) {
        setStoreProduct(null);
        setStoreProducts([]);
        storeProductsRef.current = [];
        storeProductRef.current = null;
        catalogSettledRef.current = true;
        return;
      }

      setStoreLoading(true);
      setStoreProductsLoading(true);
      catalogSettledRef.current = false;
      try {
        const listRes = await storeClient.get(
          endpoints.storeProductsByBrandName(listingBrand, 0, CATALOG_FETCH_SIZE)
        );
        const list = listRes?.data?.items || listRes?.data?.content || listRes?.data || [];
        const catalog = Array.isArray(list) ? list : [];
        storeProductsRef.current = catalog;
        setStoreProducts(catalog);

        const fromListing = String((selectedProduct as any)?.product || '').trim();
        const matched = matchListingToCatalogProduct(selectedProduct, catalog, listingBrand)?.name;
        const fromTask = String(
          selectedGroup?.taskProduct || selectedParent?.product_name || ''
        ).trim();
        const resolvedName = fromListing || matched || fromTask;

        if (resolvedName) {
          try {
            const response = await storeClient.get(
              `/api/v1/store/products/name/${encodeURIComponent(resolvedName)}/brand/${encodeURIComponent(
                listingBrand
              )}`
            );
            setStoreProduct(response.data || null);
          } catch {
            setStoreProduct(null);
          }
        } else {
          setStoreProduct(null);
        }
      } catch (error) {
        console.error('Error fetching store product:', error);
        setStoreProduct(null);
        setStoreProducts([]);
        storeProductsRef.current = [];
        storeProductRef.current = null;
      } finally {
        setStoreLoading(false);
        setStoreProductsLoading(false);
        catalogSettledRef.current = true;
      }
    };

    fetchStoreProduct();
  }, [selectedProduct, listingBrand, selectedGroup, selectedParent]);

  // Allow swapping main image via thumbnail click (listing card)
  const [mainImageOverride, setMainImageOverride] = useState<string | null>(null);
  useEffect(() => {
    // Reset override when selection changes
    setMainImageOverride(null);
  }, [selectedProduct]);

  // Separate override state for product card to avoid interfering with listing card
  const [productMainImageOverride, setProductMainImageOverride] = useState<string | null>(null);
  useEffect(() => {
    setProductMainImageOverride(null);
  }, [storeProduct]);

  // Track deleted images for the current listing
  const [deletedImages, setDeletedImages] = useState<Set<string>>(new Set());
  useEffect(() => {
    setDeletedImages(new Set());
  }, [selectedProduct]);

  // Lightbox for large image preview
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  // Description collapse state
  const [descOpen, setDescOpen] = useState(false);
  const [productDescOpen, setProductDescOpen] = useState(false);
  // Seller list expand state for selected listing card
  const [showAllSellers, setShowAllSellers] = useState(false);
  // Seller details collapse state
  const [showSellerDetails, setShowSellerDetails] = useState(false);
  // Sellers discovered via task results for this selected listing (seller pages)
  const [discoveredSellers, setDiscoveredSellers] = useState<any[]>([]);
  useEffect(() => {
    setDescOpen(false);
    setProductDescOpen(false);
    setShowAllSellers(false);
    setShowSellerDetails(false);
  }, [selectedProduct]);

  // When a product is selected, load seller page results for the same task and source URL
  useEffect(() => {
    let cancelled = false;

    const loadSellersFromTask = async () => {
      try {
        if (!selectedParent || !selectedProduct) return;
        const taskId = selectedParent.task_id;
        if (!taskId) return;
        const results = await getTaskResults(taskId);
        if (cancelled || !results || !Array.isArray(results.items)) return;

        const norm = (results.items || []) as any[];
        const src = String(selectedProduct.url || '');
        const srcBase = src.split('?')[0];
        const sellers = norm
          .filter(r => {
            const ed = r.extracted_data || {};
            const srcUrl = String(ed.source_url || '');
            const srcUrlBase = srcUrl.split('?')[0];
            return (
              (ed.page_type === 'seller' || ed.stage === 'seller') &&
              srcUrlBase &&
              srcUrlBase === srcBase
            );
          })
          .map(r => {
            const ed = r.extracted_data || {};
            return {
              name: ed.seller || ed.name || ed.seller_name,
              rating: ed.seller_rating || ed.rating,
              location: ed.location || ed.address,
              store_url: ed.seller_store_url || ed.store_url,
              url: r.url,
              screenshot_url: r.screenshot_url || ed.screenshot_url,
              contact: ed.parsed_contact, // Include parsed contact data from backend
              description: ed.description, // Include seller description
            };
          })
          .filter(s => s && (s.name || s.store_url || s.url));

        if (cancelled) return;
        setDiscoveredSellers(sellers);

        // Update the selected product with discovered sellers count for display
        if (selectedProduct && selectedGroup && sellers.length > 0) {
          setItems(prev =>
            prev.map(item => {
              if (item.id === selectedGroup.parentId) {
                return {
                  ...item,
                  products: item.products.map((p, idx) => {
                    if (idx === selectedGroup.index) {
                      return {
                        ...p,
                        discoveredSellersCount: sellers.length,
                      };
                    }
                    return p;
                  }),
                };
              }
              return item;
            })
          );
        }
      } catch (_) {
        if (!cancelled) {
          setDiscoveredSellers([]);
        }
      }
    };

    loadSellersFromTask();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParent?.id, selectedProduct?.url, selectedGroup?.groupKey]);

  const allSellersForSelected = useMemo(() => {
    if (!selectedProduct) return [];
    return collectListingSellers({
      product: selectedProduct,
      discovered: discoveredSellers,
      groupProducts: selectedGroup?.allProductsInGroup,
    });
  }, [selectedProduct, discoveredSellers, selectedGroup]);

  const countSellersInProductGroup = useCallback((entries: Array<{ product: any }>): number => {
    return collectListingSellers({
      product: entries[0]?.product,
      groupProducts: entries,
    }).length;
  }, []);

  // Snackbar feedback state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const showSuccess = (msg: string) => {
    if (batchRunningRef.current) return;
    setSnackbar({ open: true, message: msg, severity: 'success' });
  };
  const showError = (msg: string) => setSnackbar({ open: true, message: msg, severity: 'error' });

  // Delete image functionality
  const handleDeleteImage = (imageUrl: string) => {
    setDeletedImages(prev => new Set([...prev, imageUrl]));
    // If the deleted image was the main image, reset to first available image
    if (mainImageOverride === imageUrl) {
      setMainImageOverride(null);
    }
  };

  // Loading state for review actions
  const [reviewLoading, setReviewLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [injectLoading, setInjectLoading] = useState(false);

  useEffect(() => {
    // Reset loading state when selection changes
    setUpgradeLoading(false);
    setReviewLoading(false);
  }, [selectedGroupKey]);

  // Review action handlers
  const handleUpgradeListing = async () => {
    if (!selectedProduct || upgradeLoading) return;

    setUpgradeLoading(true);
    try {
      // Find parent result id for selected product
      const parent = items.find(i => i.id === (selectedGroup?.parentId || ''));
      if (!parent) return;

      // Start the upgrade process in the background
      apiClient
        .post(`/api/v1/scraper/tasks/results/${parent.id}/upgrade_product`, {
          url: selectedProduct.url,
        })
        .catch(error => {
          console.error('Background upgrade failed:', error);
          // Could show a subtle notification here if needed
        });

      // Optimistically remove search version locally immediately
      setItems(prev =>
        prev.map(it => {
          if (it.id !== parent.id) return it;
          const filtered = it.products.filter((_, idx) => idx !== (selectedGroup?.index ?? -1));
          return { ...it, products: filtered } as any;
        })
      );

      showSuccess('Upgrading to full product in background...');
    } catch (error) {
      console.error('Upgrade failed', error);
      showError('Failed to upgrade listing. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleAcceptListing = async (options?: { reload?: boolean }) => {
    if (!selectedProduct || reviewLoading) return;
    if ((selectedProduct as any)?.stage !== 'search' && !injectValidation.canInject) {
      showError(
        injectValidation.missingFields.length > 0
          ? `Cannot send to BrandTrax. Missing: ${injectValidation.missingFields.join(', ')}`
          : injectValidation.reason
      );
      return;
    }

    setReviewLoading(true);
    try {
      const currentIndex = selectedGroupKey
        ? allProducts.findIndex(g => g.groupKey === selectedGroupKey)
        : 0;
      const nextIndex = Math.min(currentIndex + 1, allProducts.length - 1);
      const nextGroup = allProducts[nextIndex];

      const injected = await handleInjectToStore();

      if (!injected) {
        throw new Error('Failed to inject listing to store');
      }

      await markListingAsReviewed('REVIEWED', options);

      if (options?.reload !== false && nextGroup) {
        setSelectedGroupKey(nextGroup.groupKey);
      }

      showSuccess('Listing accepted and added to BrandTrax');
    } catch (error) {
      console.error('Error accepting listing:', error);
      showError('Failed to accept listing');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleRejectListing = async (options?: { reload?: boolean }) => {
    if (!selectedProduct || reviewLoading) return;

    setReviewLoading(true);
    try {
      const currentIndex = selectedGroupKey
        ? allProducts.findIndex(g => g.groupKey === selectedGroupKey)
        : 0;
      const nextIndex = Math.min(currentIndex + 1, allProducts.length - 1);
      const nextGroup = allProducts[nextIndex];

      await markListingAsReviewed('IRRELEVANT', options);

      if (options?.reload !== false && nextGroup) {
        setSelectedGroupKey(nextGroup.groupKey);
      }

      showSuccess('Listing marked as irrelevant');
    } catch (error) {
      console.error('Error rejecting listing:', error);
      showError('Failed to reject listing');
    } finally {
      setReviewLoading(false);
    }
  };

  const postMarkReviewed = (parentId: string, params: Record<string, string>) =>
    apiClient.post(`/api/v1/scraper/tasks/reviews/${parentId}/mark-reviewed`, null, { params });

  const markGroupAsReviewed = async (
    group: GroupedProductEntry,
    state: string,
    options?: { reload?: boolean; clearSelection?: boolean }
  ) => {
    const reload = options?.reload !== false;
    const clearSelection = options?.clearSelection !== false;

    try {
      setItems(prevItems => {
        const next = stripGroupFromReviewItems(prevItems, group);
        itemsRef.current = next;
        allProductsRef.current = groupReviewItems(next);
        return next;
      });

      if (clearSelection) {
        setSelectedGroupKey(null);
        setSelectedListingId(null);
        setInjectGate(null);
      }

      await postMarkGroupReviewed({
        group,
        reviewState: state,
        post: postMarkReviewed,
      });

      if (!reload) {
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      setLoading(true);
      setHasMore(true);
      const q = keywordDebounced.trim() || undefined;
      const res = await getReviews(
        brand || undefined,
        stage,
        50,
        0,
        q,
        marketplaceIdsFrom(selectedPlatforms),
        product || undefined
      );
      setItems(res.items || []);

      if (res.items && res.items.length > 0) {
        const firstItem = res.items[0];
        if (firstItem.products && firstItem.products.length > 0) {
          const firstKey = getListingGroupKey(firstItem.products[0]);
          setSelectedGroupKey(firstKey);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error marking listing as reviewed:', error);
      setLoading(true);
      const q = keywordDebounced.trim() || undefined;
      const res = await getReviews(
        brand || undefined,
        stage,
        50,
        0,
        q,
        marketplaceIdsFrom(selectedPlatforms),
        product || undefined
      );
      setItems(res.items || []);
      setLoading(false);
      throw error;
    }
  };

  const markListingAsReviewed = async (state: string, options?: { reload?: boolean }) => {
    if (!selectedGroup) return;
    await markGroupAsReviewed(selectedGroup, state, options);
  };

  // Clear inject gate when a new product is selected
  useEffect(() => {
    setInjectGate(null);
  }, [selectedProduct]);

  // Auto-run inject gate on selection when enabled (manager, admin, or god)
  useEffect(() => {
    if (batchRunningRef.current) return;
    if (canUseAi && autoAi && selectedProduct && !aiLoading) {
      const timeoutId = setTimeout(() => {
        void handleAnalyzeRef.current?.();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [canUseAi, autoAi, selectedProduct, aiLoading]);

  // Keep refs to each product card for scroll-into-view
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Ensure an initial selection when data loads
  useEffect(() => {
    if (!selectedGroupKey && allProducts.length > 0) {
      const first = allProducts[0];
      setSelectedGroupKey(first.groupKey);
      setSelectedListingId(generateListingId(first.parentId, first.index));
    }
  }, [allProducts, selectedGroupKey]);

  // Scroll the selected card into view when selection changes
  useEffect(() => {
    if (!selectedGroupKey) return;
    const current = allProducts.find(g => g.groupKey === selectedGroupKey);
    if (!current) return;
    const el = cardRefs.current[current.id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedGroupKey, allProducts]);

  // Keyboard navigation: ArrowLeft / ArrowRight / ArrowUp / ArrowDown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key !== 'ArrowRight' &&
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowUp' &&
        e.key !== 'ArrowDown'
      )
        return;
      if (batchRunningRef.current || rejectingHeldRef.current) return;
      if (allProducts.length === 0) return;

      e.preventDefault();
      const currentIndex = selectedGroupKey
        ? allProducts.findIndex(g => g.groupKey === selectedGroupKey)
        : 0;

      if (e.key === 'ArrowRight') {
        const nextIndex = Math.min(currentIndex + 1, allProducts.length - 1);
        const next = allProducts[nextIndex];
        if (next) {
          setSelectedGroupKey(next.groupKey);
          setSelectedListingId(generateListingId(next.parentId, next.index));
        }
        if (nextIndex >= allProducts.length - 3 && hasMore && !loadingMore) {
          loadMore();
        }
      } else if (e.key === 'ArrowLeft') {
        const prevIndex = Math.max(currentIndex - 1, 0);
        const prev = allProducts[prevIndex];
        if (prev) {
          setSelectedGroupKey(prev.groupKey);
          setSelectedListingId(generateListingId(prev.parentId, prev.index));
        }
      } else if (e.key === 'ArrowUp') {
        // For search results: upgrade to full product
        // For product details: accept listing (inject to BrandTrax)
        if ((selectedProduct as any)?.stage === 'search') {
          handleUpgradeListing();
        } else {
          handleAcceptListing();
        }
      } else if (e.key === 'ArrowDown') {
        // Reject listing (mark as irrelevant)
        handleRejectListing();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    allProducts,
    selectedGroupKey,
    selectedProduct,
    hasMore,
    loadingMore,
    loadMore,
    handleUpgradeListing,
    handleAcceptListing,
    handleRejectListing,
  ]);

  const applyCatalogProduct = (name: string | null | undefined): string | null => {
    const catalog = storeProductsRef.current || [];
    const catalogNames = catalog.map((p: any) => String(p?.name || '').trim()).filter(Boolean);
    const resolved = resolveCatalogProductName(name, catalogNames);
    if (!resolved) return null;
    const chosen = catalog.find((p: any) => String(p?.name || '') === resolved) || null;
    overrideProductRef.current = resolved;
    setOverrideProduct(resolved);
    const brandName =
      chosen?.brand || listingBrandRef.current || reviewBrandNameRef.current || null;
    if (brandName) {
      overrideBrandRef.current = brandName;
      setOverrideBrand(brandName);
    }
    if (chosen) {
      storeProductRef.current = chosen;
      setStoreProduct(chosen);
    }
    listingProductRef.current = resolved;
    try {
      if (selectedProductRef.current) {
        selectedProductRef.current.product = resolved;
        if (brandName) selectedProductRef.current.brand = brandName;
      }
    } catch {
      // Ignore assignment errors on the listing object
    }
    return resolved;
  };

  const handleAnalyze = async (): Promise<InjectGateResult | null> => {
    const listing = selectedProductRef.current;
    if (!listing || !canUseAi) {
      return null;
    }
    setAiLoading(true);
    try {
      const images: string[] = [];
      if (Array.isArray(listing.images)) {
        listing.images
          .filter((u: any) => typeof u === 'string' && u.trim() !== '')
          .forEach((u: string) => images.push(u));
      }
      if (listing.image && !images.includes(listing.image)) {
        images.unshift(listing.image);
      }
      const catalogNames = (storeProductsRef.current || [])
        .map((p: any) => String(p?.name || '').trim())
        .filter(Boolean);
      const declaredProduct =
        overrideProductRef.current || listingProductRef.current || listing.product || undefined;
      const res = await evaluateInjectGate({
        title: listing.title,
        price: listing.price,
        url: listing.url,
        image: listing.image,
        description: String(listing.description || listing.body || ''),
        seller: String(listing.seller || listing.seller_name || ''),
        marketplace: listing.marketplace,
        brand: reviewBrandNameRef.current || listingBrandRef.current,
        product: declaredProduct || undefined,
        catalog_products: catalogNames,
        images,
      });
      injectGateRef.current = res;
      if (res.brand_ip_merchandise && !overrideProductRef.current) {
        setStoreProduct(null);
        storeProductRef.current = null;
      } else if (res.selected_product) {
        applyCatalogProduct(res.selected_product);
      }
      setInjectGate(res);
      return res;
    } catch (err) {
      setInjectGate(null);
      showError(err instanceof Error ? err.message : 'Inject gate failed');
      return null;
    } finally {
      setAiLoading(false);
    }
  };

  const handleInjectToStore = async (): Promise<boolean> => {
    if (!selectedProduct) {
      return false;
    }
    const analystProduct = (overrideProductRef.current ?? '').trim();
    const brandIpMerchandise = isBrandIpMerchandiseInject(injectGateRef.current) && !analystProduct;
    const effectiveProduct = brandIpMerchandise
      ? ''
      : (
          overrideProductRef.current ??
          (listingProductRef.current || String(selectedProduct.product || ''))
        ).trim();
    const missingForInject = injectValidation.missingFields.filter(
      field => field !== 'Store product' || !effectiveProduct
    );
    if (!injectValidation.canInject && missingForInject.length > 0) {
      const classificationError = validateClassificationBeforeSubmit(
        listingClassification,
        infringementReason,
        allowedReasonValuesFromOptions(brandInfringementOptions),
        allowedDistributionValuesFromOptions(brandInfringementOptions)
      );
      showError(
        classificationError ||
          (missingForInject.length > 0
            ? `Cannot send to BrandTrax. Missing: ${missingForInject.join(', ')}`
            : injectValidation.reason)
      );
      return false;
    }

    if (!selectedGroup) {
      return false;
    }

    setInjectLoading(true);
    try {
      const parentItem = items.find(i => i.id === selectedGroup.parentId) ?? null;
      const keywords = await keywordsForTask(parentItem?.task_id);
      const result = await injectReviewListing({
        product: selectedProduct,
        group: selectedGroup,
        catalogProduct: brandIpMerchandise ? null : storeProductRef.current,
        brand: overrideBrandRef.current ?? (listingBrandRef.current || selectedProduct.brand || ''),
        productName: brandIpMerchandise ? '' : effectiveProduct,
        analysisProfile: brandIpMerchandise ? 'BRAND_IP_MERCHANDISE' : undefined,
        productCategory: brandIpMerchandise
          ? injectGateRef.current?.article_category || undefined
          : undefined,
        keywords,
        listingClassification,
        infringementReason,
        applyAiInfringement,
        deletedImages,
        discoveredSellers,
        findMarketplaceDefinitionForUrl,
        marketplaceDefinitionId: parentItem?.marketplace_definition_id,
      });
      if (!result.ok) {
        showError(result.error || 'Failed to inject listing to store');
        return false;
      }
      if ((result.sellerCount || 0) > 1) {
        showSuccess(`Successfully injected listing with ${result.sellerCount} seller(s) to store`);
      } else if ((result.sellerCount || 0) > 0) {
        showSuccess('Successfully injected listing with seller to BrandTrax store');
      } else {
        showSuccess('Successfully injected listing to BrandTrax store');
      }
      return true;
    } catch (error) {
      console.error('Error injecting listing:', error);
      setSnackbar({
        open: true,
        message: 'Error injecting listing to store',
        severity: 'error',
      });
      return false;
    } finally {
      setInjectLoading(false);
    }
  };

  // Handler for the direct "Inject to BrandTrax" button - marks as reviewed on success
  const handleInjectAndMarkReviewed = async () => {
    try {
      const injected = await handleInjectToStore();
      if (injected) {
        await markListingAsReviewed('REVIEWED');
      }
    } catch (error) {
      console.error('Error in handleInjectAndMarkReviewed:', error);
    }
  };

  handleAnalyzeRef.current = handleAnalyze;

  const stopBatch = () => {
    batchStopRef.current = true;
    batchAbortRef.current?.abort();
  };

  const runBatch = async () => {
    if (batchRunningRef.current || rejectingHeldRef.current) return;
    if (!brand || stage !== 'stage2_product_page') {
      showError('Select a brand and Product Details to auto-process listings.');
      return;
    }
    if (!canUseAi) {
      showError('AI actions require manager, admin, or god group membership');
      return;
    }

    const owner = newAutoProcessLockOwner();
    const lock = await acquireAutoProcessLock(brand, owner, stage);
    if (!lock.acquired) {
      const message =
        lock.message ||
        `Auto-process is already running for ${brand}. Stop it in the other tab or browser.`;
      setForeignLockMessage(message);
      showError(message);
      return;
    }
    lockOwnerRef.current = owner;
    lockBrandRef.current = brand;
    setForeignLockMessage('');

    if (rejectingHeldRef.current) {
      lockOwnerRef.current = null;
      lockBrandRef.current = null;
      await releaseAutoProcessLock(brand, owner);
      return;
    }
    batchRunningRef.current = true;
    batchStopRef.current = false;
    setBatchRunning(true);
    setHeldGroups([]);
    setRejectHeldOpen(false);
    setAutoAi(false);
    setBatchStats({ injected: 0, rejected: 0, held: 0, working: 0, holdReason: '' });
    const skippedKeys = new Set<string>();
    const heldThisRun: GroupedProductEntry[] = [];
    const stats = { injected: 0, rejected: 0, held: 0, holdReason: '' };
    const holdReasons: string[] = [];
    let working = 0;
    const abort = new AbortController();
    batchAbortRef.current = abort;
    const heartbeatTimer = window.setInterval(() => {
      const lockedBrand = lockBrandRef.current || brand;
      heartbeatAutoProcessLock(lockedBrand, owner).then(ok => {
        if (!ok) {
          showError('Auto-process lock was lost. Stopping to avoid a duplicate run.');
          stopBatch();
        }
      });
    }, AUTO_PROCESS_LOCK_HEARTBEAT_MS);
    apiClient.post(endpoints.reviewsBackfillInjectGate(brand, 20000)).catch(err => {
      console.warn('Inject-gate backfill did not start:', err);
    });
    const applyAi = loadApplyAiInfringementPreference();
    const loadCatalog = createBrandCatalogCache(async (brandName: string) => {
      const listRes = await storeClient.get(
        endpoints.storeProductsByBrandName(brandName, 0, CATALOG_FETCH_SIZE)
      );
      const list = listRes?.data?.items || listRes?.data?.content || listRes?.data || [];
      return Array.isArray(list) ? list : [];
    });

    const snapshotStats = () => {
      setBatchStats({ ...stats, working });
    };

    const bump = (kind: 'injected' | 'rejected' | 'held', reason?: string) => {
      stats[kind] += 1;
      if (kind === 'held' && reason) {
        holdReasons.push(reason);
        stats.holdReason = mostCommonHoldReason(holdReasons) || reason;
      }
      snapshotStats();
    };

    const processGroup = async (group: GroupedProductEntry): Promise<AutoProcessResult> => {
      if (batchStopRef.current) return { outcome: 'skip' };
      const product = group.product;
      if (!product || (product as any).stage === 'search') return { outcome: 'skip' };

      const parentItem = itemsRef.current.find(i => i.id === group.parentId);
      const brandName = listingBrandFromGroup({
        product,
        taskBrand: group.taskBrand,
        parentBrand: parentItem?.brand_name,
        pageBrand: brandRef.current,
      });
      const catalog = brandName ? await loadCatalog(brandName) : [];
      const catalogNames = catalogNamesFrom(catalog);
      if (batchStopRef.current) return { outcome: 'skip' };

      let gate = canReuseInjectGate(
        parseStoredInjectGate((product as any).inject_gate),
        catalogNames
      );
      if (!gate) {
        try {
          gate = await evaluateInjectGate(
            {
              title: product.title,
              price: product.price,
              url: product.url,
              image: product.image,
              description: String(product.description || product.body || ''),
              seller: String(product.seller || product.seller_name || ''),
              marketplace: product.marketplace,
              brand: brandName,
              product: product.product || group.taskProduct || undefined,
              catalog_products: catalogNames,
            },
            {
              skipImages: true,
              timeoutMs: AUTO_PROCESS_GATE_TIMEOUT_MS,
              signal: abort.signal,
            }
          );
        } catch (err) {
          if (batchStopRef.current || isInjectGateAbortError(err)) {
            return { outcome: 'skip' };
          }
          console.error('Inject gate failed during auto-process:', err);
          return {
            outcome: 'held',
            reason: injectGateErrorMessage(err, AUTO_PROCESS_GATE_TIMEOUT_MS),
          };
        }
      }
      if (batchStopRef.current) return { outcome: 'skip' };
      if (!gate) return { outcome: 'held', reason: 'Inject gate returned no result' };

      if (gate.decision === 'reject') {
        await markGroupAsReviewed(group, 'IRRELEVANT', {
          reload: false,
          clearSelection: selectedGroupKeyRef.current === group.groupKey,
        });
        return { outcome: 'rejected' };
      }

      const brandIpMerchandise = isBrandIpMerchandiseInject(gate);
      if (
        !brandIpMerchandise &&
        (gate.decision === 'hold' || (gate.decision === 'inject' && gate.product !== 'pass'))
      ) {
        return {
          outcome: 'held',
          reason:
            gate.decision === 'hold'
              ? gate.summary || gate.reasons?.[0] || 'Inject gate held'
              : 'Catalog match is uncertain; left for review',
        };
      }

      const productName = brandIpMerchandise
        ? ''
        : resolveCatalogProductName(gate.selected_product, catalogNames) || '';
      const catalogProduct = brandIpMerchandise
        ? null
        : catalog.find((p: any) => String(p?.name || '') === productName) || null;
      const missing = missingAutoInjectFields({
        product,
        brand: brandName,
        productName,
        requireStoreProduct: !brandIpMerchandise,
      });
      if (missing.length > 0) {
        return { outcome: 'held', reason: `Missing ${missing.join(', ')}` };
      }

      const keywords = await keywordsForTask(parentItem?.task_id);
      const injected = await injectReviewListing({
        product,
        group,
        catalogProduct,
        brand: brandName,
        productName,
        analysisProfile: brandIpMerchandise ? 'BRAND_IP_MERCHANDISE' : undefined,
        productCategory: brandIpMerchandise ? gate.article_category || undefined : undefined,
        keywords,
        listingClassification: ListingState.NEW,
        infringementReason: '',
        applyAiInfringement: applyAi,
        findMarketplaceDefinitionForUrl,
        marketplaceDefinitionId: parentItem?.marketplace_definition_id,
      });
      if (!injected.ok) {
        return { outcome: 'held', reason: injected.error || 'Inject to BrandTrax failed' };
      }
      await markGroupAsReviewed(group, 'REVIEWED', {
        reload: false,
        clearSelection: selectedGroupKeyRef.current === group.groupKey,
      });
      return { outcome: 'injected' };
    };

    try {
      let fetchOffset = 0;
      while (!batchStopRef.current) {
        let queue = allProductsRef.current.filter(g => !skippedKeys.has(g.groupKey));
        if (queue.length === 0) {
          const q = keywordDebouncedRef.current.trim() || undefined;
          const res = await getReviewsRef.current(
            brandRef.current || undefined,
            stageRef.current,
            REVIEWS_PAGE_SIZE,
            fetchOffset,
            q,
            marketplaceIdsFrom(selectedPlatformsRef.current),
            productRef.current || undefined
          );
          const nextItems = res.items || [];
          const nextGroups = groupReviewItems(nextItems);
          itemsRef.current = nextItems;
          setItems(nextItems);
          allProductsRef.current = nextGroups;
          setHasMore(nextItems.length >= REVIEWS_PAGE_SIZE);
          queue = nextGroups.filter(g => !skippedKeys.has(g.groupKey));
          const page = nextReviewsFetchOffset({
            currentOffset: fetchOffset,
            fetchedCount: nextItems.length,
            newWorkCount: queue.length,
            pageSize: REVIEWS_PAGE_SIZE,
          });
          fetchOffset = page.offset;
          if (page.done || queue.length === 0) {
            if (page.done) break;
            continue;
          }
        }

        await mapPool(
          queue,
          AUTO_PROCESS_CONCURRENCY,
          async group => {
            working += 1;
            snapshotStats();
            try {
              const result = await processGroup(group);
              if (
                result.outcome === 'injected' ||
                result.outcome === 'rejected' ||
                result.outcome === 'held'
              ) {
                bump(result.outcome, result.reason);
              }
              if (result.outcome === 'held') {
                heldThisRun.push(group);
              }
              if (result.outcome === 'held' || result.outcome === 'skip') {
                skippedKeys.add(group.groupKey);
              }
              return result;
            } finally {
              working -= 1;
              snapshotStats();
            }
          },
          () => batchStopRef.current
        );
      }
    } catch (error) {
      console.error('Batch inject gate failed:', error);
      showError(error instanceof Error ? error.message : 'Batch processing failed');
    } finally {
      window.clearInterval(heartbeatTimer);
      await releaseAutoProcessLock(brand, owner);
      if (lockOwnerRef.current === owner) {
        lockOwnerRef.current = null;
        lockBrandRef.current = null;
      }
      if (batchAbortRef.current === abort) {
        batchAbortRef.current = null;
      }
      batchRunningRef.current = false;
      setBatchRunning(false);
      const q = keywordDebouncedRef.current.trim() || undefined;
      try {
        const res = await getReviewsRef.current(
          brandRef.current || undefined,
          stageRef.current,
          REVIEWS_PAGE_SIZE,
          0,
          q,
          marketplaceIdsFrom(selectedPlatformsRef.current),
          productRef.current || undefined
        );
        const nextItems = res.items || [];
        itemsRef.current = nextItems;
        allProductsRef.current = groupReviewItems(nextItems);
        setItems(nextItems);
        setHasMore(nextItems.length >= REVIEWS_PAGE_SIZE);
      } catch (error) {
        console.error('Failed to refresh reviews after batch:', error);
      }
      setHeldGroups(heldThisRun);
    }
  };

  const rejectHeldListings = async () => {
    const targets = rejectableHeld;
    setRejectHeldOpen(false);
    if (targets.length === 0 || rejectingHeldRef.current || batchRunningRef.current) return;

    rejectingHeldRef.current = true;
    setRejectingHeld(true);
    try {
      const { marked, failed } = await postMarkGroupsReviewed({
        groups: targets,
        reviewState: 'IRRELEVANT',
        post: postMarkReviewed,
        concurrency: 4,
      });
      if (
        selectedGroupKeyRef.current &&
        marked.some(g => g.groupKey === selectedGroupKeyRef.current)
      ) {
        setSelectedGroupKey(null);
        setSelectedListingId(null);
        setInjectGate(null);
      }
      setBatchStats(prev => ({
        ...prev,
        rejected: prev.rejected + marked.length,
        held: failed.length,
        working: 0,
        holdReason: failed.length > 0 ? prev.holdReason : '',
      }));
      setHeldGroups(failed);

      setLoading(true);
      try {
        const q = keywordDebouncedRef.current.trim() || undefined;
        const res = await getReviewsRef.current(
          brandRef.current || undefined,
          stageRef.current,
          REVIEWS_PAGE_SIZE,
          0,
          q,
          marketplaceIdsFrom(selectedPlatformsRef.current),
          productRef.current || undefined
        );
        const nextItems = res.items || [];
        itemsRef.current = nextItems;
        allProductsRef.current = groupReviewItems(nextItems);
        setItems(nextItems);
        setHasMore(nextItems.length >= REVIEWS_PAGE_SIZE);
      } catch (error) {
        console.error('Failed to refresh reviews after rejecting held listings:', error);
      } finally {
        setLoading(false);
      }

      if (failed.length > 0) {
        showError(
          marked.length === 0
            ? 'Could not reject held listings'
            : `Rejected ${marked.length} held listings. ${failed.length} could not be updated.`
        );
      } else if (marked.length === 1) {
        showSuccess('Held listing marked as irrelevant');
      } else {
        showSuccess(`Marked ${marked.length} held listings as irrelevant`);
      }
    } catch (error) {
      console.error('Failed to reject held listings:', error);
      showError('Failed to reject held listings');
    } finally {
      rejectingHeldRef.current = false;
      setRejectingHeld(false);
    }
  };

  const controlsLocked = batchRunning || rejectingHeld;
  const remainingHeld = heldGroups.length > 0 ? rejectableHeld.length : batchStats.held;

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Lightbox dialog */}
      {lightboxSrc && (
        <Box
          onClick={() => setLightboxSrc(null)}
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.8)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img
              src={lightboxSrc}
              style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 4 }}
              alt="Preview"
              onClick={e => e.stopPropagation()}
            />
            <Button
              variant="contained"
              size="small"
              onClick={e => {
                e.stopPropagation();
                window.open(lightboxSrc, '_blank', 'noopener,noreferrer');
              }}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              Open in new tab
            </Button>
          </Box>
        </Box>
      )}
      {/* Top Filter Bar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 240 }} disabled={controlsLocked}>
          <InputLabel>Brand</InputLabel>
          <Select
            label="Brand"
            value={brand}
            onChange={e => {
              setBrand(e.target.value);
              setProduct('');
            }}
            disabled={controlsLocked}
          >
            <MenuItem value="">
              <em>{loadingBrands ? 'Loading…' : 'All brands'}</em>
            </MenuItem>
            {brands.map(b => (
              <MenuItem key={b.name} value={b.name}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          size="small"
          options={catalogProducts.map(item => item.name)}
          value={product || null}
          onChange={(_, value) => setProduct(value || '')}
          disabled={controlsLocked || !brand}
          loading={loadingProducts}
          sx={{ minWidth: 280, maxWidth: 420 }}
          renderInput={params => (
            <TextField
              {...params}
              label="Product"
              placeholder={
                !brand ? 'Select a brand' : loadingProducts ? 'Loading…' : 'All products'
              }
            />
          )}
        />

        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          options={platformOptions}
          value={selectedPlatforms}
          onChange={(_, value) => setSelectedPlatforms(value)}
          getOptionLabel={option => assembleMarketplaceOptionLabel(option)}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          disabled={controlsLocked}
          sx={{ minWidth: 280, maxWidth: 440 }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip key={key} size="small" label={option.name || option.domain} {...tagProps} />
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              label="Platform"
              placeholder={selectedPlatforms.length ? '' : 'All platforms'}
            />
          )}
        />

        <TextField
          size="small"
          placeholder="Search title, body, seller, item #"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          disabled={controlsLocked}
          sx={{ minWidth: 280 }}
          inputProps={{
            'aria-label': 'Filter by keyword in listing title, body, seller, or item number',
          }}
        />

        <ToggleButtonGroup
          value={stage}
          exclusive
          onChange={(_, newStage) => newStage && setStage(newStage)}
          size="small"
          sx={{ height: 40 }}
          disabled={controlsLocked}
        >
          <ToggleButton value="stage1_search_results">Search Results</ToggleButton>
          <ToggleButton value="stage2_product_page">Product Details</ToggleButton>
        </ToggleButtonGroup>

        {loading && <CircularProgress size={18} />}
        <Typography variant="body2" color="text.secondary">
          {allProducts.length} listings found
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip
          title={
            canUseAi
              ? 'Auto-run inject gate for each selected product'
              : 'AI actions require manager, admin, or god group membership'
          }
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={autoAi}
                onChange={(_, v) => setAutoAi(v)}
                disabled={!canUseAi || controlsLocked}
              />
            }
            label={<AutoFixHighIcon sx={{ fontSize: 18 }} />}
            sx={{ ml: 1 }}
          />
        </Tooltip>
        <Tooltip
          title={
            !canUseAi
              ? 'AI actions require manager, admin, or god group membership'
              : !brand
                ? 'Select a brand to auto-process listings'
                : stage !== 'stage2_product_page'
                  ? 'Switch to Product Details to auto-inject'
                  : batchRunning
                    ? 'Stop auto-processing'
                    : foreignLockMessage
                      ? foreignLockMessage
                      : 'Auto-process listings in parallel. Reuses stored inject-gate results, pre-evaluates the rest of the brand queue on the server, then injects or rejects.'
          }
        >
          <span>
            <Button
              size="small"
              variant={batchRunning ? 'outlined' : 'contained'}
              color={batchRunning ? 'inherit' : 'primary'}
              startIcon={batchRunning ? <StopIcon /> : <PlayArrowIcon />}
              disabled={
                rejectingHeld ||
                !canUseAi ||
                !brand ||
                stage !== 'stage2_product_page' ||
                (!batchRunning && (allProducts.length === 0 || Boolean(foreignLockMessage)))
              }
              onClick={batchRunning ? stopBatch : runBatch}
            >
              {batchRunning ? 'Stop' : 'Auto-process'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {!batchRunning && foreignLockMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {foreignLockMessage}
        </Alert>
      )}

      {(batchRunning || rejectingHeld || (Boolean(batchStats.holdReason) && remainingHeld > 0)) && (
        <Alert severity={batchRunning ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {batchRunning && (
            <Typography variant="body2" sx={{ mb: 0.75 }}>
              Running in the background. Newer listings are gated here; older ones are pre-evaluated
              on the server so later pages can inject without another LLM wait.
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ flex: '1 1 auto' }}>
              Auto-processing
              {batchRunning
                ? batchStats.working > 0
                  ? `: gating ${batchStats.working}`
                  : ': loading next page'
                : ''}{' '}
              · injected {batchStats.injected} · rejected {batchStats.rejected} · held{' '}
              {remainingHeld}
              {batchStats.holdReason ? ` · ${batchStats.holdReason}` : ''}
            </Typography>
            {!batchRunning && rejectableHeld.length > 0 && (
              <Button
                size="small"
                color="warning"
                variant="outlined"
                disabled={rejectingHeld}
                onClick={() => setRejectHeldOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {rejectingHeld ? 'Rejecting…' : `Reject held (${rejectableHeld.length})`}
              </Button>
            )}
          </Box>
          {(batchRunning || rejectingHeld) && <LinearProgress sx={{ mt: 1 }} />}
        </Alert>
      )}

      <Dialog
        open={rejectHeldOpen}
        onClose={() => {
          if (!rejectingHeld) setRejectHeldOpen(false);
        }}
      >
        <DialogTitle>Reject held listings</DialogTitle>
        <DialogContent>
          <Typography>
            Mark {rejectableHeld.length} held {rejectableHeld.length === 1 ? 'listing' : 'listings'}{' '}
            as irrelevant? They leave the review queue, the same as pressing ↓ on each one.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectHeldOpen(false)} disabled={rejectingHeld}>
            Cancel
          </Button>
          <Button
            onClick={rejectHeldListings}
            color="warning"
            variant="contained"
            disabled={rejectingHeld || rejectableHeld.length === 0}
          >
            Reject held
          </Button>
        </DialogActions>
      </Dialog>

      {!batchRunning && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Keyboard shortcuts:</strong> ← → previous / next listing · ↑ send to BrandTrax
              (product details) or upgrade listing (search results) · ↓ reject as irrelevant
            </Typography>
          </Alert>

          {selectedProduct &&
            !batchRunning &&
            (selectedProduct as any).stage !== 'search' &&
            !injectValidation.canInject &&
            injectValidation.missingFields.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Cannot send this listing to BrandTrax until required fields are set:{' '}
                <strong>{injectValidation.missingFields.join(', ')}</strong>
                {(injectValidation.missingFields.includes('Brand') ||
                  injectValidation.missingFields.includes('Store product')) && (
                  <>
                    {' '}
                    — use the <strong>Store product</strong> dropdown on the listing card to choose
                    brand and product.
                  </>
                )}
              </Alert>
            )}

          {/* Horizontal Product Scrolling */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ flexGrow: 1 }} />
              {loadingMore && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading more...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              ref={scrollContainerRef}
              sx={{
                display: 'flex',
                gap: 1.5,
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                pb: 0.5, // Add some padding for better visual spacing
              }}
            >
              {allProducts.map(item => (
                <Paper
                  key={item.id}
                  elevation={selectedGroupKey === item.groupKey ? 4 : 1}
                  sx={{
                    minWidth: 160,
                    maxWidth: 160,
                    cursor: 'pointer',
                    border: selectedGroupKey === item.groupKey ? '2px solid' : '1px solid',
                    borderColor: selectedGroupKey === item.groupKey ? 'primary.main' : 'divider',
                    '&:hover': {
                      elevation: 2,
                    },
                  }}
                  ref={el => {
                    cardRefs.current[item.id] = el;
                  }}
                  onClick={() => {
                    setSelectedGroupKey(item.groupKey);
                    setSelectedListingId(generateListingId(item.parentId, item.index));
                  }}
                >
                  <CardContent sx={{ p: 1.5 }}>
                    <CardMedia
                      component="img"
                      image={
                        item.product.image ||
                        (Array.isArray((item.product as any).images) &&
                        (item.product as any).images.length > 0
                          ? (item.product as any).images[0]
                          : '/brandtrax-logo.png')
                      }
                      alt={item.product.title || 'Product'}
                      sx={{
                        width: '100%',
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                      onError={(e: any) => (e.currentTarget.style.display = 'none')}
                    />
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      sx={{
                        mb: 0.25,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        lineHeight: 1.2,
                        maxHeight: '2.4em',
                        fontSize: '0.75rem',
                      }}
                    >
                      {item.product.title || 'Untitled'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {(() => {
                        if (!item.product.price) return item.product.marketplace || '';

                        // Try to determine currency from multiple sources
                        let currency = (item.product as any).currency; // From backend cleanse_currency

                        if (!currency) {
                          // Try to detect from marketplace domain
                          const marketplace = item.product.marketplace || '';
                          if (marketplace.includes('tokopedia')) {
                            currency = 'IDR';
                          } else if (marketplace.includes('amazon')) {
                            currency = 'USD';
                          } else if (marketplace.includes('ebay')) {
                            currency = 'USD';
                          } else {
                            // Fall back to parsing the price
                            currency = parsePrice(item.product.price).currency;
                          }
                        }

                        const price = item.product.price;

                        // Clean price for Tokopedia (periods are thousands separators)
                        let cleanPrice = price;
                        if (currency === 'IDR' && price.includes('.')) {
                          cleanPrice = price.replace(/\./g, '');
                        }

                        return `${cleanPrice} ${currency}`;
                      })()}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      {new Date(item.crawledAt).toLocaleDateString()}
                    </Typography>
                    {(() => {
                      const itemNum =
                        (item.product as any).itemNumber ??
                        (item.product as any).item_number ??
                        extractItemNumberFromUrl(item.product?.url || item.url || '');
                      if (itemNum) {
                        return (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', fontSize: '0.65rem', mt: 0.25 }}
                            title="Item number (ASIN)"
                          >
                            #{itemNum}
                          </Typography>
                        );
                      }
                      return null;
                    })()}
                    {(() => {
                      const groupEntries =
                        item.allProductsInGroup?.length > 0
                          ? item.allProductsInGroup
                          : [{ product: item.product }];
                      const sellerCount = countSellersInProductGroup(groupEntries);
                      const discoveredCount = (item.product as any).discoveredSellersCount || 0;
                      const effectiveSellerCount = Math.max(sellerCount, discoveredCount);

                      if (effectiveSellerCount > 1) {
                        return (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              fontSize: '0.65rem',
                              mt: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {effectiveSellerCount} sellers
                          </Typography>
                        );
                      }

                      const singleSeller =
                        typeof (item.product as any).seller === 'string' &&
                        (item.product as any).seller.trim() !== ''
                          ? (item.product as any).seller
                          : null;

                      if (!singleSeller && discoveredCount === 0) return null;

                      return (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            fontSize: '0.65rem',
                            mt: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {singleSeller || 'Multiple sellers'}
                        </Typography>
                      );
                    })()}
                    {authorizedListingKeys.has(item.groupKey) && (
                      <Chip
                        label="Authorized seller"
                        size="small"
                        color="info"
                        sx={{ mt: 0.75, height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </CardContent>
                </Paper>
              ))}
            </Box>
          </Box>

          {/* Main 3-pane content */}
          <Grid container spacing={1.5}>
            {/* Left: selected listing details & images */}
            <Grid item xs={12} lg={injectGate ? 4 : 5}>
              <Card sx={{ height: '100%' }}>
                {selectedProduct ? (
                  <>
                    <CardHeader
                      avatar={(() => {
                        const logoUrl = marketplaceLogos.get(selectedProduct.url || '');
                        const fallbackLogoUrl =
                          logoUrl ||
                          (() => {
                            try {
                              const domain = new URL(
                                selectedProduct.url || ''
                              ).hostname.toLowerCase();
                              const baseDomain = domain.split('.').slice(-2).join('.');
                              for (const [url, logo] of marketplaceLogos.entries()) {
                                try {
                                  const logoDomain = new URL(url).hostname.toLowerCase();
                                  const logoBaseDomain = logoDomain.split('.').slice(-2).join('.');
                                  if (baseDomain === logoBaseDomain) {
                                    return logo;
                                  }
                                } catch {
                                  // Skip invalid URLs
                                }
                              }
                            } catch {
                              // Skip if URL is invalid
                            }
                            return undefined;
                          })();

                        if (!fallbackLogoUrl) return null;

                        return (
                          <Tooltip title={`Visit ${selectedProduct.marketplace || 'marketplace'}`}>
                            <Avatar
                              src={fallbackLogoUrl}
                              alt="Marketplace logo"
                              sx={{
                                width: 40,
                                height: 40,
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                try {
                                  const marketplaceUrl = new URL(selectedProduct.url || '');
                                  const baseUrl = `${marketplaceUrl.protocol}//${marketplaceUrl.hostname}`;
                                  window.open(baseUrl, '_blank');
                                } catch {
                                  // Skip if URL is invalid
                                }
                              }}
                              onError={(e: any) => (e.currentTarget.style.display = 'none')}
                            />
                          </Tooltip>
                        );
                      })()}
                      title={
                        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                          {selectedProduct.title || 'Untitled'}
                        </Typography>
                      }
                      subheader={
                        selectedProduct.price && (
                          <Typography color="primary" sx={{ fontWeight: 600 }}>
                            {(() => {
                              // Use the same currency detection and price cleansing logic as the list card
                              let currency = (selectedProduct as any).currency; // From backend cleanse_currency

                              if (!currency) {
                                // Try to detect from marketplace domain
                                const marketplace = selectedProduct.marketplace || '';
                                if (marketplace.includes('tokopedia')) {
                                  currency = 'IDR';
                                } else if (marketplace.includes('amazon')) {
                                  currency = 'USD';
                                } else if (marketplace.includes('ebay')) {
                                  currency = 'USD';
                                } else {
                                  // Fall back to parsing the price
                                  currency = parsePrice(selectedProduct.price).currency;
                                }
                              }

                              const price = selectedProduct.price;

                              // Clean price for Tokopedia (periods are thousands separators)
                              let cleanPrice = price;
                              if (currency === 'IDR' && price.includes('.')) {
                                cleanPrice = price.replace(/\./g, '');
                              }

                              return `${cleanPrice} ${currency}`;
                            })()}
                          </Typography>
                        )
                      }
                    />
                    <CardContent sx={{ p: 2, pt: 0 }}>
                      <Stack spacing={1}>
                        {/* Item number (ASIN etc.) — visible for reviewer */}
                        {(() => {
                          const itemNum =
                            (selectedProduct as any).itemNumber ??
                            (selectedProduct as any).item_number ??
                            extractItemNumberFromUrl(selectedProduct?.url || '');
                          if (!itemNum) return null;
                          return (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Item #:</strong> {itemNum}
                            </Typography>
                          );
                        })()}
                        {selectedIsAuthorizedSeller && (
                          <Chip
                            label="Authorized seller listing"
                            size="small"
                            color="info"
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        )}
                        {/* Product override selector: choose correct Store product for this listing */}
                        {listingBrand && (
                          <Box>
                            <Autocomplete
                              size="small"
                              loading={storeProductsLoading}
                              options={storeProducts.map((p: any) => p.name)}
                              value={
                                isBrandIpMerchandiseInject(injectGate) && !overrideProduct
                                  ? ''
                                  : overrideProduct || storeProduct?.name || listingProduct || ''
                              }
                              onChange={(_, newValue) => {
                                const chosen =
                                  (storeProducts || []).find((p: any) => p.name === newValue) ||
                                  null;
                                setOverrideProduct(chosen?.name || null);
                                setOverrideBrand(chosen?.brand || listingBrand || null);
                                if (chosen) setStoreProduct(chosen);
                                // Also reflect into selectedProduct association so inject uses it
                                try {
                                  (selectedProduct as any).product = chosen?.name || listingProduct;
                                  (selectedProduct as any).brand = chosen?.brand || listingBrand;
                                } catch {
                                  // Ignore parsing errors for product selection
                                }
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  label="Store product"
                                  placeholder={
                                    storeProducts.length > 0
                                      ? 'Select the correct product'
                                      : `No products loaded for ${listingBrand}`
                                  }
                                />
                              )}
                            />
                            {isBrandIpMerchandiseInject(injectGate) && !overrideProduct && (
                              <Typography variant="caption" color="text.secondary">
                                Brand-IP merchandise
                                {injectGate?.article_category
                                  ? `: ${injectGate.article_category}`
                                  : ''}
                                . No catalog product will be assigned.
                              </Typography>
                            )}
                            {overrideProduct && overrideProduct !== listingProduct && (
                              <Typography variant="caption" color="text.secondary">
                                Listing will be injected as product: {overrideProduct}
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel id="review-classification-label">Classification</InputLabel>
                            <Select
                              labelId="review-classification-label"
                              label="Classification"
                              value={listingClassification}
                              onChange={e => {
                                const next = e.target.value as ListingState;
                                setListingClassification(next);
                                if (
                                  next !== ListingState.INFRINGING &&
                                  next !== ListingState.UNAUTHORIZED_DISTRIBUTION
                                ) {
                                  setInfringementReason('');
                                }
                              }}
                            >
                              {REVIEW_CLASSIFICATION_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {listingClassification === ListingState.INFRINGING && (
                            <FormControl size="small" fullWidth required>
                              <InputLabel id="review-infringement-label" shrink>
                                Infringement
                              </InputLabel>
                              <Select
                                labelId="review-infringement-label"
                                label="Infringement"
                                value={infringementReason}
                                onChange={e => setInfringementReason(e.target.value)}
                                displayEmpty
                                renderValue={selected => {
                                  if (!selected) {
                                    return (
                                      <Typography
                                        component="span"
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ display: 'block', py: 0.25 }}
                                      >
                                        Select infringement...
                                      </Typography>
                                    );
                                  }
                                  const opt = infringementSelectOptions.find(
                                    o => o.value === selected
                                  );
                                  return opt?.label ?? selected;
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select infringement...</em>
                                </MenuItem>
                                {infringementOptionsLoading && (
                                  <MenuItem disabled value="__loading">
                                    Loading options...
                                  </MenuItem>
                                )}
                                {!infringementOptionsLoading &&
                                  infringementSelectOptions.length === 0 &&
                                  reviewBrandName && (
                                    <MenuItem disabled value="__empty">
                                      No infringement options — configure IPR in boarding
                                    </MenuItem>
                                  )}
                                {infringementSelectOptions.map(opt => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                          {listingClassification === ListingState.UNAUTHORIZED_DISTRIBUTION && (
                            <FormControl size="small" fullWidth required>
                              <InputLabel id="review-distribution-label" shrink>
                                Distribution Reason
                              </InputLabel>
                              <Select
                                labelId="review-distribution-label"
                                label="Distribution Reason"
                                value={infringementReason}
                                onChange={e => setInfringementReason(e.target.value)}
                                displayEmpty
                                renderValue={selected => {
                                  if (!selected) {
                                    return (
                                      <Typography
                                        component="span"
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ display: 'block', py: 0.25 }}
                                      >
                                        Select distribution reason...
                                      </Typography>
                                    );
                                  }
                                  const opt = distributionSelectOptions.find(
                                    o => o.value === selected
                                  );
                                  return opt?.label ?? selected;
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select distribution reason...</em>
                                </MenuItem>
                                {infringementOptionsLoading && (
                                  <MenuItem disabled value="__loading">
                                    Loading options...
                                  </MenuItem>
                                )}
                                {!infringementOptionsLoading &&
                                  distributionSelectOptions.length === 0 &&
                                  reviewBrandName && (
                                    <MenuItem disabled value="__empty">
                                      No distribution options — enable UD in boarding
                                    </MenuItem>
                                  )}
                                {distributionSelectOptions.map(opt => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={applyAiInfringement}
                                onChange={e => {
                                  const checked = e.target.checked;
                                  setApplyAiInfringement(checked);
                                  saveApplyAiInfringementPreference(checked);
                                }}
                                disabled={!canApplyAiInfringementForState(listingClassification)}
                              />
                            }
                            label="Apply AI infringement classification after analysis"
                          />
                        </Box>
                        {(() => {
                          const imgs = Array.isArray((selectedProduct as any).images)
                            ? ((selectedProduct as any).images as string[]).filter(
                                (u: any) =>
                                  typeof u === 'string' && u.trim() !== '' && !deletedImages.has(u)
                              )
                            : [];
                          const main = mainImageOverride || selectedProduct.image || imgs[0];
                          if (!main) return null;
                          return (
                            <CardMedia
                              component="img"
                              image={main}
                              alt={selectedProduct.title || 'Image'}
                              sx={{
                                width: '100%',
                                height: 260,
                                objectFit: 'contain',
                                borderRadius: 1,
                                cursor: 'zoom-in',
                              }}
                              onClick={() => setLightboxSrc(main)}
                              onError={(e: any) => (e.currentTarget.style.display = 'none')}
                            />
                          );
                        })()}
                        {(() => {
                          const imgs = Array.isArray((selectedProduct as any).images)
                            ? ((selectedProduct as any).images as string[]).filter(
                                (u: any) =>
                                  typeof u === 'string' && u.trim() !== '' && !deletedImages.has(u)
                              )
                            : [];
                          if (imgs.length === 0) return null;
                          const currentMain = mainImageOverride || selectedProduct.image || imgs[0];
                          const thumbs = imgs; // show all images so the first can be re-selected
                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'nowrap',
                                gap: 1,
                                overflowX: 'auto',
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': { display: 'none' },
                              }}
                            >
                              {thumbs.map((u, i) => (
                                <Box key={`${u}-${i}`} sx={{ position: 'relative' }}>
                                  <CardMedia
                                    component="img"
                                    image={u}
                                    alt={`thumb-${i + 1}`}
                                    sx={{
                                      width: 56,
                                      height: 56,
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      cursor: 'pointer',
                                      outline: u === currentMain ? '2px solid' : 'none',
                                      outlineColor:
                                        u === currentMain ? 'primary.main' : 'transparent',
                                    }}
                                    onClick={() => setMainImageOverride(u)}
                                    onError={(e: any) => (e.currentTarget.style.display = 'none')}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleDeleteImage(u);
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                      color: 'white',
                                      width: 16,
                                      height: 16,
                                      opacity: 0.7,
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                        opacity: 1,
                                      },
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 10 }} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          );
                        })()}
                        {/* Sellers list */}
                        {(() => {
                          const all = allSellersForSelected;
                          if (all.length === 0) return null;
                          const visible = showAllSellers ? all : all.slice(0, 3);
                          return (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Sellers
                              </Typography>
                              <Stack spacing={0.5}>
                                {visible.map((s: any, idx: number) => {
                                  const name = sellerDisplayName(s);
                                  const link = s.store_url || s.seller_store_url || s.url;
                                  return (
                                    <Typography key={idx} variant="body2" color="text.secondary">
                                      {link ? (
                                        <Link
                                          href={link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          underline="hover"
                                        >
                                          {name}
                                        </Link>
                                      ) : (
                                        name
                                      )}
                                    </Typography>
                                  );
                                })}
                              </Stack>
                              {all.length > 3 && (
                                <Button
                                  size="small"
                                  onClick={() => setShowAllSellers(v => !v)}
                                  sx={{ mt: 0.5, textTransform: 'none' }}
                                >
                                  {showAllSellers ? 'Show less' : `Show all (${all.length})`}
                                </Button>
                              )}
                            </Box>
                          );
                        })()}
                        {/* Seller Details - Show what will be sent to BrandTrax */}
                        {(() => {
                          const allSellers = allSellersForSelected;
                          if (allSellers.length === 0) return null;

                          const sellersData = allSellers.map((seller: any) => {
                            let contact = seller.contact;
                            if (!contact && seller.location) {
                              contact = { address: seller.location };
                            }
                            const country = contact?.country || null;
                            const sellerUrl =
                              seller.url || seller.store_url || seller.seller_store_url;

                            return {
                              identifier: sellerIdentifier(seller),
                              marketplace: selectedProduct.marketplace,
                              url: sellerUrl,
                              rating: seller.rating || seller.seller_rating,
                              description: seller.description,
                              brands: selectedProduct.brand ? [selectedProduct.brand] : [],
                              country: country,
                              contact: contact,
                            };
                          });

                          return (
                            <Box>
                              <Button
                                size="small"
                                onClick={() => setShowSellerDetails(v => !v)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  p: 0,
                                  minWidth: 'auto',
                                  color: 'text.secondary',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: 'primary.main',
                                  },
                                }}
                              >
                                {showSellerDetails ? '▼' : '▶'} Seller Details (
                                {sellersData.length})
                              </Button>
                              <Collapse in={showSellerDetails} timeout="auto" unmountOnExit>
                                <Box sx={{ mt: 1, pl: 2 }}>
                                  {sellersData.map((seller: any, idx: number) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        mb: 2,
                                        p: 1.5,
                                        borderRadius: 1,
                                        backgroundColor: 'action.hover',
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {seller.identifier}
                                      </Typography>
                                      {seller.description && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}
                                        >
                                          {seller.description}
                                        </Typography>
                                      )}
                                      {seller.url && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', wordBreak: 'break-all' }}
                                        >
                                          <strong>URL:</strong> {seller.url}
                                        </Typography>
                                      )}
                                      {seller.marketplace && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          <strong>Marketplace:</strong> {seller.marketplace}
                                        </Typography>
                                      )}
                                      {seller.rating && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          <strong>Rating:</strong> {seller.rating}
                                        </Typography>
                                      )}
                                      {seller.country && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          <strong>Country:</strong> {seller.country}
                                        </Typography>
                                      )}
                                      {seller.contact && (
                                        <Box sx={{ mt: 0.5 }}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', fontWeight: 600 }}
                                          >
                                            Contact:
                                          </Typography>
                                          {seller.contact.address && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ display: 'block', pl: 1 }}
                                            >
                                              {seller.contact.address}
                                            </Typography>
                                          )}
                                          {seller.contact.city && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ display: 'block', pl: 1 }}
                                            >
                                              City: {seller.contact.city}
                                            </Typography>
                                          )}
                                          {seller.contact.state && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ display: 'block', pl: 1 }}
                                            >
                                              State: {seller.contact.state}
                                            </Typography>
                                          )}
                                          {seller.contact.postal && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ display: 'block', pl: 1 }}
                                            >
                                              Postal: {seller.contact.postal}
                                            </Typography>
                                          )}
                                          {seller.contact.country && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ display: 'block', pl: 1 }}
                                            >
                                              Country: {seller.contact.country}
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                      {seller.brands && seller.brands.length > 0 && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          <strong>Brands:</strong> {seller.brands.join(', ')}
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </Collapse>
                            </Box>
                          );
                        })()}
                        {/* Removed single-line Seller display; replaced by Sellers list above */}
                        {selectedProduct &&
                          typeof (selectedProduct as any).description === 'string' &&
                          (selectedProduct as any).description.trim() !== '' && (
                            <Collapse in={descOpen} timeout="auto" unmountOnExit>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {(selectedProduct as any).description}
                              </Typography>
                            </Collapse>
                          )}
                      </Stack>
                    </CardContent>
                    {selectedProduct && selectedProduct.url && (
                      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Open listing in new tab">
                            <IconButton
                              size="small"
                              onClick={() =>
                                window.open(selectedProduct.url!, '_blank', 'noopener,noreferrer')
                              }
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                          {(selectedProduct as any).screenshot_url && (
                            <Tooltip title="Open screenshot in new tab">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  window.open(
                                    (selectedProduct as any).screenshot_url,
                                    '_blank',
                                    'noopener,noreferrer'
                                  )
                                }
                              >
                                <ScreenshotIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(selectedProduct as any).stage === 'search' && (
                            <Tooltip title="Upgrade to full product">
                              <IconButton
                                size="small"
                                disabled={upgradeLoading}
                                onClick={async () => {
                                  try {
                                    setUpgradeLoading(true);
                                    // Find parent result id for selected product
                                    const parent = items.find(
                                      i => i.id === (selectedGroup?.parentId || '')
                                    );
                                    if (!parent) return;
                                    await apiClient.post(
                                      `/api/v1/scraper/tasks/results/${parent.id}/upgrade_product`,
                                      {
                                        url: selectedProduct.url,
                                      }
                                    );
                                    // Optimistically remove search version locally; backend will reflect on next fetch
                                    setItems(prev =>
                                      prev.map(it => {
                                        if (it.id !== parent.id) return it;
                                        const filtered = it.products.filter(
                                          (_, idx) => idx !== (selectedGroup?.index ?? -1)
                                        );
                                        return { ...it, products: filtered } as any;
                                      })
                                    );
                                    showSuccess(
                                      'Upgraded to full product. It will appear on refresh.'
                                    );
                                  } catch (e) {
                                    console.error('Upgrade failed', e);
                                    showError('Failed to upgrade listing. Please try again.');
                                  } finally {
                                    setUpgradeLoading(false);
                                  }
                                }}
                              >
                                {upgradeLoading ? <CircularProgress size={16} /> : <UpgradeIcon />}
                              </IconButton>
                            </Tooltip>
                          )}
                          {(selectedProduct as any).stage !== 'search' && (
                            <Tooltip title={injectValidation.reason}>
                              <span style={{ display: 'inline-flex' }}>
                                <IconButton
                                  size="small"
                                  disabled={injectLoading || !injectValidation.canInject}
                                  onClick={handleInjectAndMarkReviewed}
                                  aria-label="Send to BrandTrax"
                                >
                                  {injectLoading ? <CircularProgress size={16} /> : <AddIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                        {selectedProduct &&
                          typeof (selectedProduct as any).description === 'string' &&
                          (selectedProduct as any).description.trim() !== '' && (
                            <Tooltip title={descOpen ? 'Hide description' : 'Show description'}>
                              <IconButton
                                size="small"
                                onClick={() => setDescOpen(v => !v)}
                                sx={{
                                  transform: descOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease-in-out',
                                }}
                              >
                                <ExpandMoreIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                      </CardActions>
                    )}
                  </>
                ) : (
                  <CardContent sx={{ p: 2 }}>
                    <Typography color="text.secondary">
                      Select a product from the list above
                    </Typography>
                  </CardContent>
                )}
              </Card>
            </Grid>

            {/* Center: Store product details */}
            <Grid item xs={12} lg={injectGate ? 4 : 5}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {storeLoading ? (
                  <CardContent sx={{ p: 2, flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Loading product details...</Typography>
                    </Box>
                  </CardContent>
                ) : storeProduct ? (
                  <>
                    <CardHeader
                      avatar={
                        <Tooltip title={`Visit ${storeProduct.brand} website`}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              if (storeProduct.url) {
                                window.open(storeProduct.url, '_blank');
                              }
                            }}
                          >
                            {storeProduct.brand?.charAt(0)?.toUpperCase() || 'B'}
                          </Avatar>
                        </Tooltip>
                      }
                      title={
                        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                          {storeProduct.name}
                        </Typography>
                      }
                      subheader={
                        <Box>
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                            {storeProduct.brand}
                          </Typography>
                          {storeProduct.msrp && (
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              MSRP: {Number(storeProduct.msrp.value).toFixed(2)}{' '}
                              {storeProduct.msrp.currency}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <CardContent sx={{ p: 2, pt: 0, flexGrow: 1 }}>
                      <Stack spacing={1}>
                        {storeProduct.images && storeProduct.images.length > 0 && (
                          <>
                            <CardMedia
                              component="img"
                              image={productMainImageOverride || storeProduct.images[0]}
                              alt={storeProduct.name || 'Product image'}
                              sx={{
                                width: '100%',
                                height: 260,
                                objectFit: 'cover',
                                borderRadius: 1,
                                cursor: 'zoom-in',
                              }}
                              onClick={() =>
                                setLightboxSrc(productMainImageOverride || storeProduct.images[0])
                              }
                              onError={(e: any) => (e.currentTarget.style.display = 'none')}
                            />
                            {storeProduct.images.length > 1 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {storeProduct.images
                                  .slice(0, 6)
                                  .map((image: string, index: number) => (
                                    <CardMedia
                                      key={index}
                                      component="img"
                                      image={image}
                                      alt={`Product image ${index + 1}`}
                                      sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 1,
                                        objectFit: 'cover',
                                        cursor: 'pointer',
                                        outline:
                                          image ===
                                          (productMainImageOverride || storeProduct.images[0])
                                            ? '2px solid'
                                            : 'none',
                                        outlineColor:
                                          image ===
                                          (productMainImageOverride || storeProduct.images[0])
                                            ? 'primary.main'
                                            : 'transparent',
                                      }}
                                      onClick={() => setProductMainImageOverride(image)}
                                      onError={(e: any) => (e.currentTarget.style.display = 'none')}
                                    />
                                  ))}
                              </Box>
                            )}
                          </>
                        )}
                        {storeProduct.category && (
                          <Chip label={storeProduct.category} size="small" variant="outlined" />
                        )}
                      </Stack>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Open product in new tab">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const url = storeProduct.url || selectedProduct?.url;
                              if (url) window.open(url, '_blank');
                            }}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {selectedProduct &&
                        typeof (selectedProduct as any).description === 'string' &&
                        (selectedProduct as any).description.trim() !== '' && (
                          <Tooltip
                            title={productDescOpen ? 'Hide description' : 'Show description'}
                          >
                            <IconButton
                              size="small"
                              onClick={() => setProductDescOpen(v => !v)}
                              sx={{
                                transform: productDescOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease-in-out',
                              }}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                    </CardActions>
                    {storeProduct &&
                      typeof storeProduct.description === 'string' &&
                      storeProduct.description.trim() !== '' && (
                        <Collapse in={productDescOpen}>
                          <CardContent sx={{ pt: 0 }}>
                            <Typography variant="body2" color="text.secondary">
                              {storeProduct.description}
                            </Typography>
                          </CardContent>
                        </Collapse>
                      )}
                  </>
                ) : selectedProduct ? (
                  <CardContent sx={{ p: 2, flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {listingProduct
                        ? `No matching product found in Store for "${listingProduct}" by "${listingBrand}"`
                        : `Select a Store product for "${listingBrand}" using the dropdown on the left`}
                    </Typography>
                  </CardContent>
                ) : (
                  <CardContent sx={{ p: 2, flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Select a product to view details
                    </Typography>
                  </CardContent>
                )}
              </Card>
            </Grid>

            {/* Right: AI analysis (compact by default) */}
            <Grid item xs={12} lg={injectGate ? 4 : 2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  {injectGate ? (
                    <Stack spacing={1.25}>
                      <Chip
                        label={
                          injectGate.decision === 'inject'
                            ? 'Ready to inject'
                            : injectGate.decision === 'reject'
                              ? 'Reject'
                              : 'Hold'
                        }
                        color={decisionChipColor(injectGate.decision)}
                        size="small"
                      />
                      <Typography variant="body2">{injectGate.summary}</Typography>
                      {injectGate.brand_ip_merchandise ? (
                        <Typography variant="caption" color="text.secondary">
                          Brand-IP merchandise
                          {injectGate.article_category ? `: ${injectGate.article_category}` : ''}
                        </Typography>
                      ) : (
                        injectGate.selected_product && (
                          <Typography variant="caption" color="text.secondary">
                            Store product: {injectGate.selected_product}
                            {injectGate.product_corrected ? ' (closest match)' : ''}
                          </Typography>
                        )
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        <Chip
                          size="small"
                          label={`Brand: ${injectGate.brand}`}
                          color={gateChipColor(injectGate.brand)}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={`Product: ${injectGate.product}`}
                          color={gateChipColor(injectGate.product)}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={`Infringement: ${injectGate.infringement}`}
                          color={gateChipColor(injectGate.infringement)}
                          variant="outlined"
                        />
                      </Box>
                      {injectGate.reasons.length > 0 && (
                        <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Reasons
                          </Typography>
                          <ul style={{ marginTop: 0, paddingLeft: 18 }}>
                            {injectGate.reasons.map((line, idx) => (
                              <li key={idx}>
                                <Typography variant="body2">{line}</Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Official BrandTrax scoring still runs after inject.
                      </Typography>
                    </Stack>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Check listing before inject
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                {selectedProduct && (
                  <CardActions sx={{ justifyContent: 'center', px: 2, pb: 2 }}>
                    <Tooltip
                      title={
                        !canUseAi
                          ? 'AI actions require manager, admin, or god group membership'
                          : aiLoading
                            ? 'Checking...'
                            : 'Run inject quality gate'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!selectedProduct || aiLoading || !canUseAi || batchRunning}
                          onClick={() => {
                            void handleAnalyze();
                          }}
                        >
                          {aiLoading ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CardActions>
                )}
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reviews;
